import { useState } from "react";
import { useQueryClient, useMutation, useQuery } from "@tanstack/react-query";
import AppLayout from "@/components/app-layout";
import { ViewOnlyBanner, usePermissions } from "@/hooks/use-permissions";
import { ExplainerVideoButton } from "@/components/explainer-video-button";
import { GuideButton } from "@/components/guide-button";
import { GuideNudgeBanner } from "@/components/guide-nudge-banner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Search, Download, DollarSign, TrendingUp, CreditCard, Clock, AlertTriangle } from "lucide-react";
import type { PaymentTransaction, Vendor, Property } from "@shared/schema";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { usePageTitle } from "@/hooks/use-page-title";

const STATUS_COLORS: Record<string, string> = {
  pending: "bg-yellow-100 text-yellow-800",
  processing: "bg-blue-100 text-blue-800",
  requires_3ds: "bg-amber-100 text-amber-800",
  initiated: "bg-blue-100 text-blue-800",
  succeeded: "bg-green-100 text-green-800",
  settled: "bg-green-100 text-green-800",
  failed: "bg-red-100 text-red-800",
  refunded: "bg-gray-100 text-gray-800",
  partially_refunded: "bg-gray-100 text-gray-800",
};

function formatCents(cents: number | null | undefined) {
  if (!cents) return "$0.00";
  return `$${(cents / 100).toFixed(2)}`;
}

export default function AppOperationsPayments() {
  const queryClient = useQueryClient();
  usePageTitle("Payments");
  const { canEdit: __canEditArea } = usePermissions();
  const { toast } = useToast();
  const readOnly = !__canEditArea("maintenance");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [vendorFilter, setVendorFilter] = useState("");
  const [refundTarget, setRefundTarget] = useState<PaymentTransaction | null>(null);
  const [refundAmount, setRefundAmount] = useState<string>("");
  const [refundReason, setRefundReason] = useState<string>("requested_by_customer");

  const txQuery = useQuery<PaymentTransaction[]>({
    queryKey: ["/api/app/operations/payment-transactions"],
  });

  const retryMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async (id: number) => {
      const r = await apiRequest("POST", `/api/app/operations/payments/${id}/retry`, {});
      return r.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/operations/payment-transactions"] });
      toast({ title: "Retry submitted" });
    },
    onError: (e: any) => toast({ title: "Retry failed", description: parseErrorMessage(e, "Could not retry."), variant: "destructive" }),
  });

  // Task #1907 — Polling fallback: ask the server to re-fetch the SMR from
  // Mercury and reconcile the row. Useful when the webhook is late or never
  // arrived (e.g. webhook misconfigured on the Mercury side).
  const reconcileMercuryMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async (id: number) => {
      const r = await apiRequest("POST", `/api/app/operations/payments/${id}/reconcile-mercury`, {});
      return r.json();
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/operations/payment-transactions"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/operations/tasks"] });
      const newStatus = data?.payment?.status;
      toast({ title: "Status refreshed", description: newStatus ? `Latest: ${newStatus}` : undefined });
    },
    onError: (e: any) => toast({ title: "Refresh failed", description: parseErrorMessage(e, "Could not refresh status."), variant: "destructive" }),
  });

  // Task #1907 — Cancel a still-pending Mercury ACH SendMoneyRequest. Server
  // cancels the SMR in Mercury, flips the row to "cancelled", and bumps the
  // task back to "completed" so it re-appears in Pending Approvals.
  const cancelMercuryMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async (id: number) => {
      const r = await apiRequest("POST", `/api/app/operations/payments/${id}/cancel`, {});
      return r.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/operations/payment-transactions"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/operations/tasks"] });
      toast({ title: "ACH payment cancelled" });
    },
    onError: (e: any) => toast({ title: "Cancel failed", description: parseErrorMessage(e, "Could not cancel."), variant: "destructive" }),
  });

  const refundMutation = useMutation({
    mutationFn: async ({ id, amountCents, reason }: { id: number; amountCents?: number; reason?: string }) => {
      const r = await apiRequest("POST", `/api/app/operations/payments/${id}/refund`, { amountCents, reason });
      return r.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/operations/payment-transactions"] });
      setRefundTarget(null);
      setRefundAmount("");
      toast({ title: "Refund submitted" });
    },
    onError: (e: any) => toast({ title: "Refund failed", description: parseErrorMessage(e, "Could not refund."), variant: "destructive" }),
  });

  const vendorsQuery = useQuery<Vendor[]>({
    queryKey: ["/api/app/operations/vendors"],
    queryFn: async () => {
      const res = await fetch("/api/app/operations/vendors", { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  const propertiesQuery = useQuery<Property[]>({
    queryKey: ["/api/app/properties"],
  });

  const transactions = txQuery.data || [];
  const vendors = vendorsQuery.data || [];
  const properties = propertiesQuery.data || [];

  const vendorMap = Object.fromEntries(vendors.map(v => [v.id, v]));
  const propertyMap = Object.fromEntries(properties.map(p => [p.id, p]));

  const filtered = transactions.filter(tx => {
    if (statusFilter && tx.status !== statusFilter) return false;
    if (vendorFilter && String(tx.vendorId) !== vendorFilter) return false;
    return true;
  });

  const totalPaid = transactions.filter(t => t.status === "settled").reduce((s, t) => s + (t.hostTotalCents || 0), 0);
  const totalPending = transactions.filter(t => t.status === "pending" || t.status === "initiated").reduce((s, t) => s + (t.hostTotalCents || 0), 0);
  const totalFees = transactions.filter(t => t.status === "settled").reduce((s, t) => s + (t.stripeFeeCents || 0), 0);
  const txCount = transactions.length;

  const handleExportCSV = () => {
    const headers = ["Date", "Vendor", "Property", "Vendor Amount", "Fee", "Host Total", "Status"];
    const rows = filtered.map(tx => [
      tx.createdAt ? new Date(tx.createdAt).toLocaleDateString() : "",
      vendorMap[tx.vendorId]?.name || `Vendor #${tx.vendorId}`,
      tx.propertyId ? (propertyMap[tx.propertyId]?.name || `Property #${tx.propertyId}`) : "",
      formatCents(tx.vendorAmountCents),
      formatCents(tx.stripeFeeCents),
      formatCents(tx.hostTotalCents),
      tx.status || "pending",
    ]);
    const csv = [headers, ...rows].map(r => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `payment-transactions-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <AppLayout>
      <div className="p-6 max-w-6xl mx-auto" data-testid="page-payments">
        <ViewOnlyBanner area="maintenance" />
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900" data-testid="text-page-title" data-tour="operations-payments-heading">Payment History</h1>
            <p className="text-sm text-gray-500 mt-1">Track all vendor payments and fees</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" onClick={handleExportCSV} data-testid="button-export-csv" data-tour="operations-payments-export">
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
            <ExplainerVideoButton pageKey="operations-payments" />
            <GuideButton pageKey="operations-payments" />
          </div>
        </div>
        <GuideNudgeBanner pageKey="operations-payments" />

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
                <DollarSign className="h-4 w-4" />
                Total Paid
              </div>
              <p className="text-xl font-semibold text-gray-900" data-testid="text-total-paid">{formatCents(totalPaid)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
                <Clock className="h-4 w-4" />
                Pending
              </div>
              <p className="text-xl font-semibold text-yellow-600" data-testid="text-total-pending">{formatCents(totalPending)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
                <CreditCard className="h-4 w-4" />
                PadPulse Fees
              </div>
              <p className="text-xl font-semibold text-gray-900" data-testid="text-total-fees">{formatCents(totalFees)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
                <TrendingUp className="h-4 w-4" />
                Transactions
              </div>
              <p className="text-xl font-semibold text-gray-900" data-testid="text-tx-count">{txCount}</p>
            </CardContent>
          </Card>
        </div>

        <div className="flex flex-wrap gap-3 mb-4">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[150px]" data-testid="select-status-filter" data-tour="operations-payments-list">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="initiated">Initiated</SelectItem>
              <SelectItem value="settled">Settled</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
            </SelectContent>
          </Select>
          <Select value={vendorFilter} onValueChange={setVendorFilter}>
            <SelectTrigger className="w-[180px]" data-testid="select-vendor-filter" data-tour="vendor-payments-filters">
              <SelectValue placeholder="Vendor" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Vendors</SelectItem>
              {vendors.map(v => (
                <SelectItem key={v.id} value={String(v.id)}>{v.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {txQuery.isLoading && (
          <div className="space-y-2">
            {[1, 2, 3].map(i => <Skeleton key={i} className="h-12 w-full" />)}
          </div>
        )}

        {filtered.length === 0 && !txQuery.isLoading && (
          <Card>
            <CardContent className="py-12 text-center">
              <DollarSign className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 font-medium">No transactions yet</p>
              <p className="text-sm text-gray-400 mt-1">Payment transactions will appear here after approving tasks</p>
            </CardContent>
          </Card>
        )}

        <Dialog open={!!refundTarget} onOpenChange={(open) => { if (!open && refundMutation.isPending) return; if (!open) { setRefundTarget(null); setRefundAmount(""); } }}>
          <DialogContent
            className="max-h-[90vh] overflow-y-auto"
            data-testid="dialog-refund"
            onEscapeKeyDown={(e) => { if (refundMutation.isPending) e.preventDefault(); }}
            onInteractOutside={(e) => { if (refundMutation.isPending) e.preventDefault(); }}
            onPointerDownOutside={(e) => { if (refundMutation.isPending) e.preventDefault(); }}
          >
            <DialogHeader>
              <DialogTitle>Refund payment</DialogTitle>
            </DialogHeader>
            {refundTarget && (
              <div className="space-y-3">
                <div className="text-sm text-gray-600">
                  Vendor: <span className="font-medium text-gray-900">{vendorMap[refundTarget.vendorId]?.name || `Vendor #${refundTarget.vendorId}`}</span>
                </div>
                <div className="text-sm text-gray-600">
                  Charged: <span className="font-medium text-gray-900">{formatCents(refundTarget.hostTotalCents)}</span>
                  {(refundTarget.refundedAmountCents || 0) > 0 && (
                    <span className="text-gray-500"> · already refunded {formatCents(refundTarget.refundedAmountCents)}</span>
                  )}
                </div>
                <div>
                  <label className="text-sm text-gray-600 block mb-1">Refund amount (USD)</label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={refundAmount}
                    onChange={(e) => setRefundAmount(e.target.value)}
                    data-testid="input-refund-amount"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    PadPulse fee and the vendor transfer are reversed proportionally.
                  </p>
                </div>
                <div>
                  <label className="text-sm text-gray-600 block mb-1">Reason</label>
                  <Select value={refundReason} onValueChange={setRefundReason}>
                    <SelectTrigger data-testid="select-refund-reason"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="requested_by_customer">Requested by customer</SelectItem>
                      <SelectItem value="duplicate">Duplicate</SelectItem>
                      <SelectItem value="fraudulent">Fraudulent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setRefundTarget(null)} data-testid="button-refund-cancel">Cancel</Button>
              <Button
                disabled={refundMutation.isPending || !refundTarget || !refundAmount || Number(refundAmount) <= 0}
                onClick={() => {
                  if (!refundTarget) return;
                  refundMutation.mutate({
                    id: refundTarget.id,
                    amountCents: Math.round(Number(refundAmount) * 100),
                    reason: refundReason,
                  });
                }}
                data-testid="button-refund-confirm"
              >
                {refundMutation.isPending ? "Refunding..." : "Refund"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {filtered.length > 0 && (
          <Card>
            <CardContent className="p-0 overflow-x-auto">
              <Table className="min-w-[760px]">
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Vendor</TableHead>
                    <TableHead>Property</TableHead>
                    <TableHead className="text-right">Vendor Amt</TableHead>
                    <TableHead className="text-right">PadPulse Fee</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map(tx => {
                    const refundedTotal = tx.refundedAmountCents || 0;
                    const remaining = (tx.hostTotalCents || 0) - refundedTotal;
                    const canRefund = !readOnly
                      && (tx.status === "succeeded" || tx.status === "settled" || tx.status === "partially_refunded")
                      && tx.disputeStatus !== "open"
                      && remaining > 0;
                    const canRetry = !readOnly && tx.status === "failed";
                    const canCancelMercury = !readOnly
                      && tx.paymentRail === "mercury_ach"
                      && tx.status === "awaiting_mercury_approval";
                    const canReconcileMercury = tx.paymentRail === "mercury_ach"
                      && (tx.status === "awaiting_mercury_approval" || tx.status === "processing");
                    return (
                      <TableRow key={tx.id} data-testid={`row-transaction-${tx.id}`}>
                        <TableCell className="text-sm text-gray-600">
                          {tx.createdAt ? new Date(tx.createdAt).toLocaleDateString() : "-"}
                        </TableCell>
                        <TableCell className="font-medium">
                          {vendorMap[tx.vendorId]?.name || `Vendor #${tx.vendorId}`}
                        </TableCell>
                        <TableCell className="text-sm text-gray-600">
                          {tx.propertyId ? (propertyMap[tx.propertyId]?.name || "-") : "-"}
                        </TableCell>
                        <TableCell className="text-right text-sm">{formatCents(tx.vendorAmountCents)}</TableCell>
                        <TableCell className="text-right text-sm text-gray-500">{formatCents(tx.stripeFeeCents)}</TableCell>
                        <TableCell className="text-right font-medium">{formatCents(tx.hostTotalCents)}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 flex-wrap">
                            <Badge className={STATUS_COLORS[tx.status || "pending"] || "bg-gray-100 text-gray-800"} data-testid={`badge-status-${tx.id}`}>
                              {tx.status || "pending"}
                            </Badge>
                            {tx.disputeStatus === "open" && (
                              <Badge className="bg-rose-100 text-rose-800" data-testid={`badge-dispute-${tx.id}`}>
                                <AlertTriangle className="h-3 w-3 mr-1" /> dispute
                              </Badge>
                            )}
                            {(tx.disputeStatus === "won" || tx.disputeStatus === "lost") && (
                              <Badge className={tx.disputeStatus === "won" ? "bg-emerald-100 text-emerald-800" : "bg-gray-200 text-gray-700"} data-testid={`badge-dispute-${tx.id}`}>
                                dispute {tx.disputeStatus}
                              </Badge>
                            )}
                            {refundedTotal > 0 && (
                              <span className="text-xs text-gray-500" data-testid={`text-refunded-${tx.id}`}>
                                -{formatCents(refundedTotal)}
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            {canRetry && (
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={retryMutation.isPending}
                                onClick={() => retryMutation.mutate(tx.id)}
                                data-testid={`button-retry-${tx.id}`}
                              >
                                Retry
                              </Button>
                            )}
                            {canReconcileMercury && (
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={reconcileMercuryMutation.isPending}
                                onClick={() => reconcileMercuryMutation.mutate(tx.id)}
                                data-testid={`button-reconcile-mercury-${tx.id}`}
                                title="Re-check status with Mercury (use if the status looks stuck)"
                              >
                                Refresh status
                              </Button>
                            )}
                            {canCancelMercury && (
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={cancelMercuryMutation.isPending}
                                onClick={() => cancelMercuryMutation.mutate(tx.id)}
                                data-testid={`button-cancel-mercury-${tx.id}`}
                              >
                                Cancel ACH
                              </Button>
                            )}
                            {canRefund && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => { setRefundTarget(tx); setRefundAmount(((remaining) / 100).toFixed(2)); setRefundReason("requested_by_customer"); }}
                                data-testid={`button-refund-${tx.id}`}
                              >
                                Refund
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </div>
    </AppLayout>
  );
}
